import { rememberExams, setActiveExam, useActiveExam, type ProfileExam } from "../lib/activeExam";

interface ExamSwitcherProps {
  /** The student's profiles; nothing renders unless they hold two. */
  exams: { examType: ProfileExam }[] | undefined;
  active: ProfileExam | null | undefined;
}

/**
 * JAMB | WAEC, for a student preparing for both.
 *
 * Switching is like switching accounts: every screen — subjects, readiness,
 * practice, tutor — becomes that exam's. Hidden for a student who sits one,
 * since a toggle with a single option is a question with no answer.
 */
export function ExamSwitcher({ exams: given, active }: ExamSwitcherProps) {
  // Kept up while a switch loads, when this screen has no data of its own yet.
  const exams = rememberExams(given);
  // The tap shows at once; the screen's data follows when it has loaded.
  const chosen = useActiveExam() ?? active;
  if (!exams || exams.length < 2) return null;

  return (
    <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-2">
      <span className="text-[12px] text-text-muted">Preparing for</span>
      <div
        role="group"
        aria-label="Exam"
        className="flex rounded-[var(--radius-chip)] border border-border bg-surface p-0.5"
      >
        {exams.map(({ examType }) => {
          const on = examType === chosen;
          return (
            <button
              key={examType}
              type="button"
              aria-pressed={on}
              onClick={() => setActiveExam(examType)}
              className={`min-h-9 min-w-16 rounded-[var(--radius-chip)] px-3 text-[12.5px] ${
                on ? "bg-accent-tint font-semibold text-accent" : "text-text-muted"
              }`}
            >
              {examType}
            </button>
          );
        })}
      </div>
    </div>
  );
}
